import React, {useState} from "react";
import {
    DollarSign,
    CreditCard,
    Wallet,
    Receipt,
    Smartphone,
    Landmark,
    Gift,
    CreditCard as Card2,
    Banknote
} from "lucide-react";
import {api} from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import {useQueryClient} from "@tanstack/react-query";
import {Modal, ModalFooter, FormField, SwitchRow, inputClass} from "./SettingsUI";

const PAYMENT_ICONS = [
    {name: "creditcard", icon: CreditCard, label: "Carte de crédit"},
    {name: "cash", icon: DollarSign, label: "Espèces"},
    {name: "wallet", icon: Wallet, label: "Portefeuille"},
    {name: "receipt", icon: Receipt, label: "Reçu"},
    {name: "mobile", icon: Smartphone, label: "Mobile"},
    {name: "bank", icon: Landmark, label: "Transfert bancaire"},
    {name: "gift", icon: Gift, label: "Carte cadeau"},
    {name: "debit", icon: Card2, label: "Carte de débit"},
    {name: "check", icon: Banknote, label: "Chèque"},
    {name: "default", icon: CreditCard, label: "Par défaut"},
];

const PaymentEditor = ({payment, onClose, showNotification}) => {
    const [formData, setFormData] = useState({
        name: payment ? payment.name : "",
        icon: payment ? payment.icon : "receipt",
        active: payment ? payment.active : true,
    });
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const queryClient = useQueryClient();

    const handleChange = (e) => {
        const {name, value, type, checked} = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));

        if (errors[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: null,
            }));
        }
    };

    const selectIcon = (iconName) => {
        setFormData(prev => ({...prev, icon: iconName}));
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.name.trim()) {
            newErrors.name = "Le nom du mode de paiement est requis";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setSubmitting(true);

        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            const payload = {
                name: formData.name,
                icon: formData.icon,
                active: formData.active,
            };

            if (payment) {
                // Update existing payment
                await api.put(`/payements/${payment.id}`, payload, {
                    headers: {
                        "X-account-id": accountId.id,
                        "Content-Type": "application/json",
                    },
                });
                showNotification("success", "Mode de paiement mis à jour avec succès");
            } else {
                // Create new payment
                await api.post("/payements", payload, {
                    headers: {
                        "X-account-id": accountId.id,
                        "Content-Type": "application/json",
                    },
                });
                showNotification("success", "Mode de paiement créé avec succès");
            }

            queryClient.invalidateQueries({queryKey: ["payments"]});
            onClose();
        } catch (error) {
            const errorMessage =
                error.response?.data?.detail ||
                error.response?.data?.message ||
                "Une erreur s'est produite";
            showNotification("error", `Erreur: ${errorMessage}`);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            title={payment ? "Modifier le mode de paiement" : "Nouveau mode de paiement"}
            onClose={onClose}
            footer={<ModalFooter onCancel={onClose} form="payment-form" saving={submitting}
                                 label={payment ? "Enregistrer" : "Créer"}/>}
        >
            <form id="payment-form" onSubmit={handleSubmit} className="space-y-4">
                <FormField id="name" label="Nom" required error={errors.name}>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={inputClass(errors.name)}
                        placeholder="ex: Carte de crédit"
                    />
                </FormField>

                <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Icône :</span>
                    <div className="grid grid-cols-4 gap-2 md:grid-cols-5">
                        {PAYMENT_ICONS.map((iconObj) => {
                            const IconComponent = iconObj.icon;
                            const selected = formData.icon === iconObj.name;
                            return (
                                <button
                                    key={iconObj.name}
                                    type="button"
                                    onClick={() => selectIcon(iconObj.name)}
                                    aria-pressed={selected}
                                    className={`flex flex-col items-center p-3 rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-primary ${
                                        selected
                                            ? "border-primary bg-primary/10 text-primary"
                                            : "border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                                    }`}
                                    title={iconObj.label}
                                >
                                    <IconComponent className="w-5 h-5"/>
                                    <span className="text-xs mt-1">{iconObj.label.split(" ").pop()}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <SwitchRow id="active" label="Actif" checked={formData.active}
                           onChange={(checked) => setFormData(prev => ({...prev, active: checked}))}/>
            </form>
        </Modal>
    );
};

export default PaymentEditor;
